import { useRef, useMemo, useEffect } from "react";
import { rotateUVs } from "@/services/helpers/rotateUV";
import * as THREE from "three";
import { PlankProps } from "@/services/interfaces";
import { useAppStore } from "@/stores/appStore";

export default function DoorPlank({
	position,
	width,
	height,
	distributedHeight,
	length,
	pillarPosition,
	textures,
}: PlankProps) {
	const { gate } = useAppStore((state) => state.configurator);
	const meshRef = useRef();
	const lastGeometryRef = useRef<THREE.BufferGeometry | null>(null);

	useEffect(() => {
		const mesh = meshRef.current;
		if (!mesh) return;

		const geometry = mesh.geometry;
		if (!geometry) return;

		// Check if the geometry is new
		if (lastGeometryRef.current !== geometry) {
			// Store the new geometry reference
			lastGeometryRef.current = geometry;
		}
	}, [width, height, length]); // Runs only when geometry might change

	let shape = new THREE.Shape()
		.moveTo(0, 0)
		.lineTo(0, distributedHeight.low[1])
		.lineTo(width, distributedHeight.high[1])
		.lineTo(width, 0)
		.closePath();

	// Create geometry and dispose of the old one before re-creating
	const geometry = useMemo(() => {
		const extrudeSettings = {
			depth: length,
			bevelEnabled: true,
			bevelSegments: 5,
			steps: 1,
			bevelSize: 0.0015,
			bevelThickness: 0.003,
			curveSegments: 12,
		};

		// Dispose of the previous geometry if it exists
		if (lastGeometryRef.current) {
			lastGeometryRef.current.dispose();
		}

		const newGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

		rotateUVs(newGeometry, {
			front: 180,
			left: 270,
			right: 270,
			back: 180,
			top: 0,
			bottom: 0,
		});

		// Update the reference to the new geometry
		lastGeometryRef.current = newGeometry;
		return newGeometry;
	}, [shape, length]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (lastGeometryRef.current) {
				lastGeometryRef.current.dispose();
			}
		};
	}, []);

	return (
		<mesh
			castShadow
			receiveShadow
			geometry={geometry}
			position={[position.x - width / 2, position.y - height / 2, -length / 2]}>
			<meshStandardMaterial
				{...textures}
				color={gate.selectedMaterials[gate.id]?.settings.color}
				roughness={gate.selectedMaterials[gate.id]?.settings.roughness}
				normalScale={new THREE.Vector2(pillarPosition === "left" ? -1 : 1, 1)}
			/>
		</mesh>
	);
}
